"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuthenticatedFetch } from "@/hooks/useAuthenticatedFetch";

interface User {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: "OWNER" | "ROOT" | "ADMIN" | "USER";
  active: boolean;
  confirmed: boolean;
}

export function UserManagementPage() {
  const { isAdmin, loading, isSignedIn, isRoot } = useUserRole();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [users, setUsers] = useState<User[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  // Redirect if not admin
  useEffect(() => {
    if (!loading && !isSignedIn) {
      navigate("/", { replace: true });
    }
    if (!loading && isSignedIn && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [loading, isAdmin, isSignedIn, navigate]);

  useEffect(() => {
    async function fetchData() {
      if (!isAdmin) return;
      try {
        const usersData = await authenticatedFetch<User[]>("/admin/users");
        setUsers(usersData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoadingData(false);
      }
    }
    if (isAdmin) {
      void fetchData();
    }
  }, [isAdmin, authenticatedFetch]);

  const handleRoleChange = async (userId: number, newRole: string) => {
    if (!isRoot) {
      alert("Only ROOT or OWNER users can change roles");
      return;
    }

    setUpdatingUserId(userId);
    try {
      await authenticatedFetch(`/admin/users/${userId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: newRole }),
      });
      const usersData = await authenticatedFetch<User[]>("/admin/users");
      setUsers(usersData);
      alert("Role updated successfully");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleToggleActive = async (userId: number, currentActive: boolean) => {
    setUpdatingUserId(userId);
    try {
      const endpoint = currentActive
        ? `/admin/users/${userId}/deactivate`
        : `/admin/users/${userId}/activate`;

      await authenticatedFetch(endpoint, { method: "PUT" });
      const usersData = await authenticatedFetch<User[]>("/admin/users");
      setUsers(usersData);
    } catch (err) {
      alert(
        err instanceof Error ? err.message : "Failed to update user status"
      );
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (loading || loadingData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-950 p-4 sm:p-6 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Manage Users</h1>
            <p className="text-sm text-gray-400">
              Admin tasks for roles and activation.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              onClick={() => navigate("/admin")}
              className="w-full rounded-md bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-600 sm:w-auto"
            >
              Back to Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-900 p-4 text-red-200">
            {error}
          </div>
        )}

        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h2 className="mb-4 text-xl font-semibold text-white">
            User Management
          </h2>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800 text-left">
                  <th className="pb-3 text-sm font-semibold text-gray-400">
                    Email
                  </th>
                  <th className="pb-3 text-sm font-semibold text-gray-400">
                    Name
                  </th>
                  <th className="pb-3 text-sm font-semibold text-gray-400">
                    Role / Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-800">
                    <td className="py-3 text-sm text-gray-300">{user.email}</td>
                    <td
                      className={`py-3 text-sm ${
                        user.active ? "text-green-200" : "text-red-200"
                      }`}
                    >
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap items-center gap-3">
                        {isRoot && user.role !== "OWNER" ? (
                          <select
                            value={user.role}
                            onChange={(e) => {
                              void handleRoleChange(user.id, e.target.value);
                            }}
                            disabled={updatingUserId === user.id}
                            className="rounded bg-gray-800 px-2 py-1 text-sm text-white"
                          >
                            <option value="USER">USER</option>
                            <option value="ADMIN">ADMIN</option>
                            <option value="ROOT">ROOT</option>
                            {isRoot && <option value="OWNER">OWNER</option>}
                          </select>
                        ) : (
                          <span
                            className={`rounded px-2 py-1 text-sm ${
                              user.role === "OWNER"
                                ? "bg-yellow-900 text-yellow-200"
                                : user.role === "ROOT"
                                  ? "bg-purple-900 text-purple-200"
                                  : user.role === "ADMIN"
                                    ? "bg-blue-900 text-blue-200"
                                    : "bg-gray-800 text-gray-300"
                            }`}
                          >
                            {user.role}
                          </span>
                        )}
                        {user.role !== "OWNER" && (
                          <button
                            onClick={() => {
                              void handleToggleActive(user.id, user.active);
                            }}
                            disabled={updatingUserId === user.id}
                            className="rounded bg-gray-700 px-3 py-1.5 text-sm text-white hover:bg-gray-600 disabled:opacity-50"
                          >
                            {updatingUserId === user.id
                              ? "..."
                              : user.active
                                ? "Deactivate"
                                : "Activate"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {users.map((user) => (
              <div
                key={user.id}
                className="rounded-lg border border-gray-800 bg-gray-900 p-4 shadow-sm"
              >
                <div className="text-sm font-semibold text-white break-words">
                  {user.email}
                </div>
                <div
                  className={`text-sm ${
                    user.active ? "text-green-200" : "text-red-200"
                  }`}
                >
                  {user.firstName} {user.lastName}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  {isRoot && user.role !== "OWNER" ? (
                    <select
                      value={user.role}
                      onChange={(e) => {
                        void handleRoleChange(user.id, e.target.value);
                      }}
                      disabled={updatingUserId === user.id}
                      className="rounded bg-gray-800 px-3 py-2 text-sm text-white"
                    >
                      <option value="USER">USER</option>
                      <option value="ADMIN">ADMIN</option>
                      <option value="ROOT">ROOT</option>
                      {isRoot && <option value="OWNER">OWNER</option>}
                    </select>
                  ) : (
                    <span
                      className={`rounded px-2 py-1 text-sm ${
                        user.role === "OWNER"
                          ? "bg-yellow-900 text-yellow-200"
                          : user.role === "ROOT"
                            ? "bg-purple-900 text-purple-200"
                            : user.role === "ADMIN"
                              ? "bg-blue-900 text-blue-200"
                              : "bg-gray-800 text-gray-300"
                      }`}
                    >
                      {user.role}
                    </span>
                  )}

                  {user.role !== "OWNER" && (
                    <button
                      onClick={() => {
                        void handleToggleActive(user.id, user.active);
                      }}
                      disabled={updatingUserId === user.id}
                      className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-600 disabled:opacity-50"
                    >
                      {updatingUserId === user.id
                        ? "Updating..."
                        : user.active
                          ? "Deactivate"
                          : "Activate"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
